import { faChevronDown } from "@fortawesome/pro-solid-svg-icons";
import { Button, ButtonGroup, ButtonTypeMap, Menu, MenuProps } from "@mui/material";
import { ExtendButtonBaseTypeMap } from "@mui/material/ButtonBase";
import { OverrideProps } from "@mui/material/OverridableComponent";
import { SxProps, Theme } from "@mui/material/styles";
import {
  ElementType,
  FC,
  KeyboardEvent,
  MouseEvent,
  ReactElement,
  ReactNode,
  useEffect,
  useRef,
  useState,
} from "react";
import { sr25Amber, sr25Blue, sr25Green, sr25Gray, sr25Red } from "../../../theming/sr25Colors";
import { FaIcon } from "../../DataDisplay/Icon/FaIcon";
import { SplitButtonMenuItem, SplitButtonMenuItemProps } from "./components/SplitButtonMenuItem";

type SplitButtonOption = {
  label: string;
  disabled?: boolean;
};

type SplitButtonPropsBase<C extends ElementType> = Omit<
  OverrideProps<ExtendButtonBaseTypeMap<ButtonTypeMap<unknown, C>>, C>,
  "onClick"
>;

export type SplitButtonProps<C extends ElementType = "button"> = SplitButtonPropsBase<C> & {
  options: (string | SplitButtonOption)[];
  /** Option index preselected on mount (uncontrolled; out-of-range or disabled values fall back to the first enabled option). */
  defaultSelectedIndex?: number;
  hideSelectedOption?: boolean;
  menuAnchorOrigin?: Partial<NonNullable<MenuProps["anchorOrigin"]>>;
  menuTransformOrigin?: Partial<NonNullable<MenuProps["transformOrigin"]>>;
  variant?: "text" | "outlined" | "contained";
  onClick: (event: MouseEvent<HTMLButtonElement>, selectedOption: string) => void;
  onItemClick?: (event: MouseEvent<HTMLLIElement>, index: number, option: string) => void;
  startIcon?: ReactNode;
  sx?: SxProps<Theme>;
  showMoreOptionsAriaLabel?: string;
};

export interface SplitButtonComponent extends FC<SplitButtonProps> {
  Item: FC<SplitButtonMenuItemProps>;
}

const DEFAULT_MENU_ANCHOR_ORIGIN: NonNullable<MenuProps["anchorOrigin"]> = {
  vertical: "bottom",
  horizontal: "right",
};

const DEFAULT_MENU_TRANSFORM_ORIGIN: NonNullable<MenuProps["transformOrigin"]> = {
  vertical: "top",
  horizontal: "right",
};

export const SplitButton: SplitButtonComponent = <C extends ElementType = "button">({
  options,
  defaultSelectedIndex,
  hideSelectedOption,
  menuAnchorOrigin,
  menuTransformOrigin,
  disabled,
  variant,
  size,
  color,
  onClick,
  onItemClick,
  startIcon,
  children,
  sx,
  showMoreOptionsAriaLabel = "Show more options",
  ...props
}: SplitButtonProps<C>): ReactElement => {
  const [open, setOpen] = useState(false);
  const anchorRef = useRef<HTMLDivElement>(null);
  const [selectedIndex, setSelectedIndex] = useState(defaultSelectedIndex ?? 0);

  // Normalize options to a safe array to avoid runtime errors on undefined
  const safeOptions = Array.isArray(options) ? options : ([] as (string | SplitButtonOption)[]);

  // Be defensive: gracefully handle undefined options and objects without label
  const getOptionLabel = (option?: string | SplitButtonOption) => {
    if (typeof option === "string") return option;
    if (option && typeof option === "object") return option.label ?? "";
    return "";
  };

  const isOptionDisabled = (option?: string | SplitButtonOption) =>
    !!(option && typeof option !== "string" && option.disabled);

  // When options change (e.g., external state toggles disabling), ensure the selected option is valid.
  useEffect(() => {
    // No options: reset to 0 and bail out
    if (!Array.isArray(options) || options.length === 0) {
      if (selectedIndex !== 0) setSelectedIndex(0);
      return;
    }

    // Ensure selectedIndex within bounds
    let nextIndex = selectedIndex;
    if (nextIndex >= options.length || nextIndex < 0) {
      nextIndex = 0;
    }

    // If current selected option is disabled, fallback to first enabled option.
    const current = options[nextIndex];
    if (!current || isOptionDisabled(current)) {
      const firstEnabledIndex = options.findIndex((opt) => !isOptionDisabled(opt));
      nextIndex = firstEnabledIndex >= 0 ? firstEnabledIndex : 0;
    }

    if (nextIndex !== selectedIndex) setSelectedIndex(nextIndex);
  }, [options, selectedIndex]);

  const handleMenuItemClick = (event: MouseEvent<HTMLLIElement>, index: number) => {
    setSelectedIndex(index);
    setOpen(false);
    if (onItemClick) {
      onItemClick(event, index, getOptionLabel(safeOptions[index]));
    }
  };

  const handleToggle = () => {
    setOpen((prevOpen) => !prevOpen);
  };

  const handleMainButtonClick = (event: MouseEvent<HTMLButtonElement>) => {
    onClick(event, getOptionLabel(safeOptions?.[selectedIndex]));
  };

  const handleListKeyDown = (event: KeyboardEvent) => {
    if (event.key === "Tab") {
      event.preventDefault();
      setOpen(false);
    } else if (event.key === "Escape") {
      setOpen(false);
    }
  };

  const displayedLabel = children || getOptionLabel(safeOptions?.[selectedIndex]);

  return (
    <>
      <ButtonGroup
        {...props}
        ref={anchorRef}
        disabled={disabled}
        variant={variant}
        size={size}
        color={color}
        sx={[
          {
            "& .MuiButtonGroup-grouped": {
              minWidth: 30,
            },
          },
          ...(Array.isArray(sx) ? sx : [sx]),
        ]}
      >
        <Button
          onClick={handleMainButtonClick}
          startIcon={startIcon}
          size={size}
          sx={(theme) => ({
            "&.MuiButtonGroup-grouped": {
              borderColor: theme.vars.palette.stroke.transparent,
            },
            "&.MuiButton-colorPrimary.MuiButtonGroup-grouped, &.MuiButton-colorInfo.MuiButtonGroup-grouped":
              {
                borderColor: sr25Blue[700],
              },
            "&.MuiButton-colorError.MuiButtonGroup-grouped": {
              borderColor: sr25Red[700],
            },
            "&.MuiButton-colorSuccess.MuiButtonGroup-grouped": {
              borderColor: sr25Green[700],
            },
            "&.MuiButton-colorWarning.MuiButtonGroup-grouped": {
              borderColor: sr25Amber[700],
            },
            "&.Mui-disabled.MuiButtonGroup-grouped": {
              borderColor: sr25Gray[200],
            },
            "&.MuiButtonGroup-grouped.Mui-disabled": {
              borderColor: theme.vars.palette.stroke.transparentDim,
            },
          })}
        >
          {displayedLabel}
        </Button>
        <Button
          onClick={handleToggle}
          aria-haspopup="menu"
          aria-expanded={open}
          aria-controls={open ? "split-button-menu" : undefined}
          aria-label={showMoreOptionsAriaLabel}
          sx={{
            "&.MuiButtonGroup-grouped": {
              px: 0,
              borderColor: "transparent",
            },
          }}
        >
          <FaIcon icon={faChevronDown} />
        </Button>
      </ButtonGroup>
      <Menu
        id="split-button-menu"
        open={open}
        anchorEl={anchorRef.current}
        onClose={() => setOpen(false)}
        anchorOrigin={{ ...DEFAULT_MENU_ANCHOR_ORIGIN, ...menuAnchorOrigin }}
        transformOrigin={{
          ...DEFAULT_MENU_TRANSFORM_ORIGIN,
          ...menuTransformOrigin,
        }}
        MenuListProps={{
          onKeyDown: handleListKeyDown,
        }}
      >
        {safeOptions.map((option, index) => {
          if (!option) return null;
          if (safeOptions.length === 1 || !hideSelectedOption || index !== selectedIndex) {
            return (
              <SplitButtonMenuItem
                key={getOptionLabel(option)}
                label={getOptionLabel(option)}
                disabled={isOptionDisabled(option)}
                selected={index === selectedIndex}
                onClick={(event) => handleMenuItemClick(event, index)}
              />
            );
          }
          return null;
        })}
      </Menu>
    </>
  );
};

SplitButton.Item = SplitButtonMenuItem;
